/**
 * PrintTechnologyMismatchDialog (BLU-1509): blocking amber dialog for a shirt
 * scanned at the wrong print station. The operator goes to the other station
 * or switches the shirt here (the safe in-place flip; the server decides).
 * A refusal leaves only the way out, with "Ask a coordinator"; `busy` and a
 * lost connection keep the switch button. Focus starts on the way out, so a
 * stray scanner Enter never switches a shirt. PRODUCTION_DESIGN §4, §6.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ArrowLeftRight, Loader2, XCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { playErrorBeep } from '@/lib/sounds';
import { trpcErrorCode } from '@/lib/trpc-error-utils';
import {
  otherPrintTechnology,
  type PrintTechnology,
} from '@/features/production-orders/utils/print-tech-routing';
import {
  isRetryableSwitchRefusal,
  switchRefusalKey,
} from '@/features/production-tracker/lib/print-technology-switch';
import { PretreatWarning } from './PretreatWarning';

/** What the page's switch call came back with. `errorCode` null = not allowed here. */
export type PrintTechnologySwitchAttempt = { ok: true } | { ok: false; errorCode: string | null };

export interface PrintTechnologyMismatchDialogProps {
  open: boolean;
  /** What the shirt is set to. This station serves the other technology. */
  shirtTechnology: PrintTechnology;
  /** DTG target only: the shirt needs pretreat before it is printed. */
  needsPretreat?: boolean;
  /** False for a role that cannot open the other station (DTF Printer cannot open DTG). */
  canGoToStation: boolean;
  onGoToStation: () => void;
  /**
   * Flip the shirt to this station's technology. Resolve `{ ok: true }` once
   * the page has taken over (it closes the dialog), or the refusal code.
   * Throw on a transport failure; FORBIDDEN reads as "not allowed here".
   */
  onSwitch: () => Promise<PrintTechnologySwitchAttempt>;
  onClose: () => void;
}

type Phase = 'ask' | 'switching' | 'refused' | 'retry';

const AMBER_ACTION = 'bg-amber-600 hover:bg-amber-700 text-white focus-visible:ring-amber-600';
const MUTED_ACTION = 'bg-muted text-foreground hover:bg-muted/80 focus-visible:ring-amber-600';
const ACTION = 'h-auto min-h-14 w-full sm:w-auto px-6 py-3 text-lg font-semibold whitespace-normal text-center gap-2';

/** Copy keys by the technology this station switches to. */
const STATION_COPY: Record<PrintTechnology, { body: string; goTo: string; switchTo: string }> = {
  DTG: { body: 'scanRouting.switch.bodyAtDtg', goTo: 'scanRouting.switch.goToFilm', switchTo: 'scanRouting.switch.toDtg' },
  DTF: { body: 'scanRouting.switch.bodyAtFilm', goTo: 'scanRouting.switch.goToDtg', switchTo: 'scanRouting.switch.toDtf' },
};

export function PrintTechnologyMismatchDialog({
  open,
  shirtTechnology,
  needsPretreat = false,
  canGoToStation,
  onGoToStation,
  onSwitch,
  onClose,
}: PrintTechnologyMismatchDialogProps) {
  const { t } = useTranslation('production');
  const [phase, setPhase] = useState<Phase>('ask');
  const [refusalCode, setRefusalCode] = useState<string | null>(null);
  const switchingRef = useRef(false);
  const wayOutRef = useRef<HTMLButtonElement>(null);

  const target = otherPrintTechnology(shirtTechnology);
  const copyKeys = STATION_COPY[target];
  const showPretreat = needsPretreat && target === 'DTG';
  const isSwitching = phase === 'switching';
  const refusalKey = switchRefusalKey(refusalCode);

  // A fresh question every time it opens, with focus on the way out
  // (ScanErrorModal / SupersededStickerDialog ergonomics).
  useEffect(() => {
    if (!open) return;
    setPhase('ask');
    setRefusalCode(null);
    switchingRef.current = false;
    const timer = setTimeout(() => wayOutRef.current?.focus(), 10);
    return () => clearTimeout(timer);
  }, [open]);

  const handleSwitch = async () => {
    if (switchingRef.current) return; // a second tap before the re-render
    switchingRef.current = true;
    setPhase('switching');
    setRefusalCode(null);
    let next: Phase;
    let code: string | null = null;
    try {
      const outcome = await onSwitch();
      if (outcome.ok) return; // the page closes the dialog and takes over
      code = outcome.errorCode;
      next = isRetryableSwitchRefusal(code) ? 'retry' : 'refused';
    } catch (error) {
      next = trpcErrorCode(error) === 'FORBIDDEN' ? 'refused' : 'retry';
    } finally {
      switchingRef.current = false;
    }
    // Audio twin of the panel below (PRODUCTION_DESIGN §5.2): the operator is
    // likely looking at the shirt, not the screen.
    playErrorBeep();
    setRefusalCode(code);
    setPhase(next);
  };

  const handleWayOut = () => {
    if (canGoToStation) onGoToStation();
    else onClose();
  };

  const wayOutLabel = canGoToStation ? t(copyKeys.goTo) : t('scanFeedback.ok');

  return (
    <AlertDialog open={open} onOpenChange={(next) => { if (!next && !isSwitching) onClose(); }}>
      <AlertDialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <AlertDialogHeader className="space-y-4">
          <div className="flex items-center gap-3">
            <AlertTriangle
              className="h-8 w-8 sm:h-10 sm:w-10 flex-shrink-0 text-amber-600 dark:text-amber-400"
              aria-hidden="true"
            />
            <AlertDialogTitle className="text-2xl sm:text-3xl font-bold text-amber-700 dark:text-amber-400">
              <span className="sr-only">{t('scanFeedback.warning')}</span>
              {t('scanRouting.switch.title', { tech: shirtTechnology })}
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-lg sm:text-xl text-foreground">
            {t(copyKeys.body)}
          </AlertDialogDescription>
        </AlertDialogHeader>

        {/* The buzz on open is its audio twin. */}
        {showPretreat && <PretreatWarning data-testid="mismatch-pretreat" />}

        {phase === 'refused' && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-100"
          >
            <XCircle className="mt-0.5 h-6 w-6 shrink-0 text-red-700 dark:text-red-300" aria-hidden="true" />
            <div className="min-w-0 space-y-1 text-base sm:text-lg">
              {refusalKey && <p>{t(refusalKey, { tech: target })}</p>}
              <p className="font-semibold">{t('scanRouting.switch.askCoordinator')}</p>
            </div>
          </div>
        )}

        {phase === 'retry' && (
          <div
            role="alert"
            className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100"
          >
            <AlertTriangle className="mt-0.5 h-6 w-6 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden="true" />
            <p className="min-w-0 text-base sm:text-lg">
              {refusalKey ? t(refusalKey, { tech: target }) : t('scanRouting.switch.failed')}
            </p>
          </div>
        )}

        <AlertDialogFooter className="gap-2">
          <Button
            ref={wayOutRef}
            onClick={handleWayOut}
            disabled={isSwitching}
            className={cn(ACTION, phase === 'refused' ? AMBER_ACTION : MUTED_ACTION)}
          >
            {wayOutLabel}
          </Button>
          {phase !== 'refused' && (
            <Button onClick={() => void handleSwitch()} disabled={isSwitching} className={cn(ACTION, AMBER_ACTION)}>
              {isSwitching ? (
                <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden="true" />
              ) : (
                <ArrowLeftRight className="h-5 w-5 shrink-0" aria-hidden="true" />
              )}
              {t(isSwitching ? 'scanRouting.switch.switching' : copyKeys.switchTo)}
            </Button>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
